import { z } from 'zod';

import { validateClientsParamsSetup, type ClientsParamsSetupValues } from './clients-params.js';
import {
    resolveClientContextCustomerName,
    validateNewPartition,
    validateOnboardingCreate,
    type ClientContextErrorCode,
    type OnboardingCreateValues,
} from './onboarding.js';

// ─── New Organization onboarding ─────────────────────────────────────
// Optional on New Organization. When sent, the customer partition, Client
// Industry and the clients_params setup are validated before the tenant is
// provisioned; when skipped, only the Nexus organization is created.

const MAX_CUSTOMER_PARTITION_LENGTH = 255;

export const NewOrganizationOnboardingSchema = z
    .object({
        customerPartition: z.string().max(MAX_CUSTOMER_PARTITION_LENGTH),
        idempotencyKey: z.string().uuid(),
        compKey: z.string().trim().min(1).max(255),
        fields: z.record(z.unknown()),
        setup: z.record(z.unknown()),
    })
    .strict();

export type NewOrganizationOnboardingInput = z.infer<typeof NewOrganizationOnboardingSchema>;

export interface NewOrganizationOnboardingValues {
    customerName: string;
    customerPartition: string;
    idempotencyKey: string;
    compKey: string;
    fields: OnboardingCreateValues;
    setup: ClientsParamsSetupValues;
}

export type NewOrganizationOnboardingValidation =
    | { ok: true; values: NewOrganizationOnboardingValues }
    | { ok: false; code: ClientContextErrorCode; message: string; field?: string };

/** Both partitions are new here: New Organization is where a customer is first created. */
export function validateNewOrganizationOnboarding(input: NewOrganizationOnboardingInput): NewOrganizationOnboardingValidation {
    const customer = resolveClientContextCustomerName(input.customerPartition);
    if (!customer.ok) return customer;

    const partition = validateNewPartition(customer.customerName, 'customerPartition') ?? validateNewPartition(input.compKey, 'compKey');
    if (partition) return partition;

    const fields = validateOnboardingCreate(input.fields);
    if (!fields.ok) return fields;

    const setup = validateClientsParamsSetup(input.setup);
    if (!setup.ok) return setup;

    return {
        ok: true,
        values: {
            customerName: customer.customerName,
            customerPartition: input.customerPartition.trim(),
            idempotencyKey: input.idempotencyKey,
            compKey: input.compKey.trim(),
            fields: fields.values,
            setup: setup.values,
        },
    };
}

// ─── Customer partition check (New Organization review) ──────────────

/** The partition the review screen checks before the organization is created. */
export const CustomerPartitionCheckBodySchema = z
    .object({ customerPartition: z.string().trim().min(1).max(MAX_CUSTOMER_PARTITION_LENGTH) })
    .strict();
export type CustomerPartitionCheckBody = z.infer<typeof CustomerPartitionCheckBodySchema>;

/**
 * Organizations that already resolve one of the same BigQuery clients, using
 * the same matcher as the provisioning guard (comma-split, trimmed, exact).
 * Empty `usedBy` means the partition is free in Nexus. Answered immediately;
 * whether the customer exists in BigQuery arrives later from the data-sync check.
 */
export const CustomerPartitionUsageSchema = z.object({
    customerPartition: z.string(),
    customerName: z.string(),
    clients: z.array(z.string()),
    usedBy: z.array(z.object({ tenantId: z.string(), name: z.string() })),
});
export type CustomerPartitionUsage = z.infer<typeof CustomerPartitionUsageSchema>;
